import type { Axis } from 'highcharts/highcharts.src';
import type { AxisPlotLinePathOptionsObject, ChartRenderCallbackFunction, Options } from 'highcharts/highstock';
import Highcharts from 'highcharts/highstock';
import moment from 'moment';
import {
    chartColors,
    DEFAULT_CHART_NAVIGATOR_RANGE,
    FONT_FAMILY_VERLAG,
    FONT_SIZE_MD,
    FONT_SIZE_SM,
    FONT_WEIGHT_BOLD,
    FONT_WEIGHT_REGULAR,
    X_AXIS_OFFSET,
    Y_AXIS_OFFSET,
} from '../../../config/constants';
import type { ITooltipSeries } from '../../../interfaces/chart';
import type { HighchartCallbackFn, ICashflowOptionParams } from '../../../interfaces/projection';

(function (H) {
    H.wrap((H as any).Tick.prototype, 'getMarkPath', function (this: any, _proceed, x, y) {
        return this.mark.renderer.arc(x, y + 2.5, 2, 0, -Math.PI / 2, (Math.PI / 2) * 3).d.split(' ');
    });

    H.wrap((H as any).Axis.prototype, 'drawCrosshair', function (this: any, proceed) {
        const prevFunction = H.Axis.prototype.getPlotLinePath;
        H.Axis.prototype.getPlotLinePath = function (this: any, options: AxisPlotLinePathOptionsObject) {
            const translatedValue = Math.min(Math.max(-1e5, options.translatedValue!), 1e5);
            const x = Math.round(translatedValue + this.transB);
            const yTop = this.chart.yAxis[0].toPixels(this.chart.yAxis[0].max);
            const yBottom = this.chart.yAxis[0].toPixels(this.chart.yAxis[0].min);

            return this.chart.renderer.crispLine(['M', x, yTop, 'V', yBottom + X_AXIS_OFFSET], options.lineWidth || 1);
        };
        proceed.apply(this, Array.prototype.slice.call(arguments, 1));
        H.Axis.prototype.getPlotLinePath = prevFunction;
    });
})(Highcharts);

export const getChartOptions = (params: ICashflowOptionParams): Options => {
    const addPointClickHandler = (seriesData: []) => {
        return seriesData.map((series: Highcharts.SeriesOptionsType) => {
            if (series.type === 'line' || series.type === 'spline') {
                series.point = {
                    events: {
                        click: function (this: Highcharts.Point) {
                            const seriesList: ITooltipSeries[] = [];
                            this.series.chart.series.forEach((series) => {
                                if (!series['name'].startsWith('Navigator')) {
                                    seriesList.push({
                                        name: series['name'],
                                        // @ts-expect-error Problems with missing Highcharts types
                                        color: series['color'],
                                        value: series['data'][this.index].y ?? 0,
                                        type: series['type'],
                                    });
                                }
                            });

                            params?.onClickPointHandler?.({
                                title: moment(this.x).toString(),
                                time: moment(this.x),
                                position: { x: this.plotX ?? 0, y: this.plotY ?? 0 },
                                series: seriesList,
                            });
                        },
                    },
                };
            }

            return series;
        });
    };

    return {
        navigator: {
            xAxis: {
                gridLineColor: 'var(--fg-color-default-invert)',
                left: 0,
                labels: {
                    x: 10,
                    y: -15,
                    style: {
                        fontSize: FONT_SIZE_MD,
                        fontWeight: `${FONT_WEIGHT_BOLD}`,
                        color: 'var(--fg-color-default-normal)',
                    },
                },
            },
            yAxis: {
                plotBands: [
                    {
                        from: -Number.MAX_VALUE,
                        to: Number.MAX_VALUE,
                        color: 'var(--bg-color-default-stronger)',
                    },
                ],
            },
            handles: {
                symbols: ['url(handler.svg)', 'url(handler.svg)'],
            },
            series: {
                type: 'line',
                data: params.seriesData[2]?.data,
            },
            enabled: true,
            maskFill: '#feffd957',
            outlineColor: 'rgba(176, 170, 126, 0)',
        },
        chart: {
            animation: false,
            events: {
                load(this: Highcharts.Chart) {
                    try {
                        params.onChartLoadEventHandlers.forEach((handler) => {
                            handler(this);
                        });
                    } catch {
                        return;
                    }
                },
                render: function (this: Highcharts.Chart) {
                    const xAxis = this.xAxis[0] as Axis;
                    // @ts-expect-error Problems with missing Highcharts types
                    params.refs.xAxisYPosition.current = xAxis['bottom'] - xAxis['offset'];
                    params.onChartRenderEventHandlers.forEach((handler: HighchartCallbackFn<Highcharts.Chart>) => {
                        handler(this);
                    });
                } as ChartRenderCallbackFunction,
            },
            spacingLeft: 5,
            backgroundColor: '#F6F6F6',
        },
        accessibility: { enabled: false },
        credits: { enabled: false },
        title: {
            text: 'hidden test',
            margin: 1,
            style: {
                color: '#F6F6F6',
                fontSize: FONT_SIZE_SM,
            },
        },
        yAxis: {
            offset: Y_AXIS_OFFSET,
            tickLength: 50,
            gridLineColor: chartColors.GRID_LINE,
            gridLineWidth: 1,
            maxPadding: 0.1,
            plotLines: [
                {
                    width: 2,
                    value: 0,
                    zIndex: 999,
                },
            ],
            title: {
                text: '',
            },
            labels: {
                style: {
                    color: chartColors.LABEL,
                    fontSize: FONT_SIZE_SM,
                    fontFamily: FONT_FAMILY_VERLAG,
                    fontWeight: `${FONT_WEIGHT_REGULAR}`,
                },
                y: -5,
            },
        },
        xAxis: {
            minRange: DEFAULT_CHART_NAVIGATOR_RANGE,
            crosshair: params.crosshairXaxis ? { color: chartColors.CROSSHAIR, width: 2 } : false,
            tickLength: 10,
            className: 'highcharts-cashflow-x-axis',
            startOnTick: false,
            offset: X_AXIS_OFFSET,
            labels: {
                formatter: params.xAxisLabelsFormatterFn,
                y: 30,
            },
            type: 'datetime',
            units: [['year', null]],
            min: moment(params.yearRange.from, 'yyyy').valueOf(),
            max: moment(params.yearRange.to, 'yyyy').valueOf(),
        },
        legend: {
            enabled: true,
            floating: true,
        },
        tooltip: {
            useHTML: true,
            outside: true,
            shape: 'rect',
            distance: 35,
            shared: true,
            padding: 10,
            backgroundColor: 'var(--fg-color-default-invert)',
            formatter: params.tooltipFormatterFn,
            hideDelay: 0,
        },
        series: addPointClickHandler(params.seriesData),
        plotOptions: {
            column: {
                grouping: false,
                shadow: false,
                borderWidth: 0,
            },
            series: {
                marker: { enabled: false },
                pointStart: moment(params.yearRange.from).valueOf(),
                pointIntervalUnit: 'year',
                events: {
                    legendItemClick: () => false,
                },
            },
        },
    };
};
